import { useEffect, useImperativeHandle } from 'react';
import {
  PhoneNumberInput,
  Stack,
  Text,
  TextInput,
} from '@adc/parallax-component-library';
import { useForm, useStore } from '@tanstack/react-form';

import { PatientInformationInviteLabels } from '~/features/patientCreation/constants';
import type {
  PatientInformationFormProps,
  PatientInformationInviteLabelsKeys,
} from '~/features/patientCreation/types';
import { patientInformationSchema } from '~/features/patientCreation/validations';
import { buildTestId } from '~/utils/buildTestId';

import DatePicker from '../DatePicker';

const [firstInviteLabelKey] = Object.keys(
  PatientInformationInviteLabels,
) as unknown as PatientInformationInviteLabelsKeys[];

export const PatientInformationForm = ({
  ref,
  onSubmit,
  setIsNextDisabled,
  defaultValues,
  testID,
}: PatientInformationFormProps) => {
  const { matchType } = defaultValues;
  const form = useForm({
    defaultValues: {
      id: defaultValues.id ?? '',
      email: defaultValues.email ?? '',
      phone: defaultValues.phone ?? '',
      firstName: defaultValues.firstName ?? '',
      lastName: defaultValues.lastName ?? '',
      dateOfBirth: defaultValues.dateOfBirth ?? '',
      zipCode: defaultValues.zipCode ?? '',
      mrn: defaultValues.mrn ?? '',
      sendInvite: defaultValues.sendInvite ?? true,
      invite: firstInviteLabelKey ?? '',
    },
    validators: {
      onMount: patientInformationSchema,
      onChange: patientInformationSchema,
    },
    onSubmit: ({ value }) => {
      onSubmit(value);
    },
  });

  const isNextDisabled = useStore(
    form.store,
    (state) => !state.canSubmit || state.isSubmitting,
  );

  useEffect(() => {
    setIsNextDisabled(isNextDisabled);
  }, [isNextDisabled, setIsNextDisabled]);

  useImperativeHandle(
    ref,
    () => ({
      submitForm: form.handleSubmit,
    }),
    [form.handleSubmit],
  );

  return (
    <Stack gap="$4" paddingTop="$2" testID={testID}>
      <Text
        type="$bodyBase.medium"
        color="$text.100"
        paddingBottom="$2"
        testID={buildTestId({ testID, suffix: 'title' })}
      >
        Patient Information
      </Text>
      {/* Disabled row, subject to change */}
      <Stack flexDirection="row" gap="$6">
        <Stack flex={1} flexShrink={1} flexBasis="0%">
          <form.Field name="phone">
            {(field) => (
              <PhoneNumberInput
                testID={buildTestId({
                  testID,
                  suffix: ['input', 'phone'],
                })}
                label="Phone Number"
                value={field.state.value}
                onChangeText={field.handleChange}
                onBlur={field.handleBlur}
                inputMode="tel"
                autoCapitalize="none"
                autoCorrect={false}
                disabled={matchType === undefined || matchType === 'phone'}
                error={field.state.meta.isTouched && !field.state.meta.isValid}
                supportingText={
                  field.state.meta.isTouched && !field.state.meta.isValid
                    ? field.state.meta.errors[0]?.message
                    : undefined
                }
              />
            )}
          </form.Field>
        </Stack>
        <Stack flex={1} flexShrink={1} flexBasis="0%">
          <form.Field name="email">
            {(field) => (
              <TextInput
                testID={buildTestId({
                  testID,
                  suffix: ['input', 'email'],
                })}
                aria-label="Email"
                label="Email"
                placeholder="Email"
                value={field.state.value}
                onChangeText={field.handleChange}
                onBlur={field.handleBlur}
                inputMode="email"
                autoCapitalize="none"
                autoCorrect={false}
                error={field.state.meta.isTouched && !field.state.meta.isValid}
                supportingText={
                  field.state.meta.isTouched && !field.state.meta.isValid
                    ? field.state.meta.errors[0]?.message
                    : undefined
                }
              />
            )}
          </form.Field>
        </Stack>
      </Stack>

      {/* First / Last Name */}
      <Stack flexDirection="row" gap="$6">
        <Stack flex={1} flexShrink={1} flexBasis="0%">
          <form.Field name="firstName">
            {(field) => (
              <TextInput
                testID={buildTestId({
                  testID,
                  suffix: ['input', 'first', 'name'],
                })}
                aria-label="First Name"
                label="First Name"
                placeholder="First Name"
                value={field.state.value}
                onChangeText={field.handleChange}
                onBlur={field.handleBlur}
                autoCapitalize="words"
                autoCorrect={false}
                error={field.state.meta.isTouched && !field.state.meta.isValid}
                supportingText={
                  field.state.meta.isTouched && !field.state.meta.isValid
                    ? field.state.meta.errors[0]?.message
                    : undefined
                }
              />
            )}
          </form.Field>
        </Stack>
        <Stack flex={1} flexShrink={1} flexBasis="0%">
          <form.Field name="lastName">
            {(field) => (
              <TextInput
                testID={buildTestId({
                  testID,
                  suffix: ['input', 'last', 'name'],
                })}
                aria-label="Last Name"
                label="Last Name"
                placeholder="Last Name"
                value={field.state.value}
                onChangeText={field.handleChange}
                onBlur={field.handleBlur}
                autoCapitalize="words"
                autoCorrect={false}
                error={field.state.meta.isTouched && !field.state.meta.isValid}
                supportingText={
                  field.state.meta.isTouched && !field.state.meta.isValid
                    ? field.state.meta.errors[0]?.message
                    : undefined
                }
              />
            )}
          </form.Field>
        </Stack>
      </Stack>

      {/* DOB / Zip */}
      <Stack flexDirection="row" gap="$6">
        <Stack flex={1} flexShrink={1} flexBasis="0%" position="relative">
          <form.Field name="dateOfBirth">
            {(field) => (
              <DatePicker
                testID={buildTestId({
                  testID,
                  suffix: ['input', 'date', 'of', 'birth'],
                })}
                label="Date of Birth"
                value={field.state.value}
                onChangeText={field.handleChange}
                onBlur={field.handleBlur}
                placeholder="MM/DD/YYYY"
                handleChange={field.handleChange}
                error={field.state.meta.isTouched && !field.state.meta.isValid}
                supportingText={
                  field.state.meta.isTouched && !field.state.meta.isValid
                    ? field.state.meta.errors[0]?.message
                    : undefined
                }
              />
            )}
          </form.Field>
        </Stack>
        <Stack flex={1} flexShrink={1} flexBasis="0%">
          <form.Field name="zipCode">
            {(field) => (
              <TextInput
                testID={buildTestId({
                  testID,
                  suffix: ['input', 'zip', 'code'],
                })}
                aria-label="Patient Zip Code"
                label="Patient ZIP Code (optional)"
                placeholder="Zip"
                value={field.state.value}
                onChangeText={field.handleChange}
                onBlur={field.handleBlur}
                autoCapitalize="none"
                autoCorrect={false}
                error={field.state.meta.isTouched && !field.state.meta.isValid}
                supportingText={
                  field.state.meta.isTouched && !field.state.meta.isValid
                    ? field.state.meta.errors[0]?.message
                    : undefined
                }
              />
            )}
          </form.Field>
        </Stack>
      </Stack>

      {/* MRN */}
      <Stack flexDirection="row" gap="$6">
        <Stack flex={1} flexShrink={1} flexBasis="0%">
          <form.Field name="mrn">
            {(field) => (
              <TextInput
                testID={buildTestId({
                  testID,
                  suffix: ['input', 'mrn'],
                })}
                aria-label="Medical Record Number"
                label="Medical Record Number"
                placeholder="Medical Record Number"
                value={field.state.value}
                onChangeText={field.handleChange}
                onBlur={field.handleBlur}
                autoCapitalize="none"
                autoCorrect={false}
                error={field.state.meta.isTouched && !field.state.meta.isValid}
                supportingText={
                  field.state.meta.isTouched && !field.state.meta.isValid
                    ? field.state.meta.errors[0]?.message
                    : undefined
                }
              />
            )}
          </form.Field>
        </Stack>
      </Stack>
    </Stack>
  );
};
